// node modules
import { observable, action, computed } from 'mobx';
import { computedFn } from 'mobx-utils';
// typings
import { SegmentType } from 'modules/Search/constants';
import {
  CustomerStatus,
  ProfileResponse,
  UserPreferenceSettings,
  UserProfile,
  UserSegment,
  UserType,
} from './typings';
// modules
import { CookieStorage } from '../Storage/modules/CookieStorage';

export default class UserStore {
  // properties
  @observable isDepthDataAvailable = false;
  @observable profile: UserProfile | null = null;
  @observable userPreferenceSettings: UserPreferenceSettings | null = null;
  @observable profileId = CookieStorage.getItem('profile_id_web') || CookieStorage.getItem('profile_id') || '';
  @observable ucc = CookieStorage.getItem('user_id') || '';
  @observable customerStatus: CustomerStatus =
    (CookieStorage.getItem('customer_status') as CustomerStatus) || 'ACTIVE';

  @observable userType = CookieStorage.getItem('user_type') || 'CUSTOMER';
  @observable bankList: ProfileResponse['userBankList'] | null = null;
  @observable productsEnabled: ProfileResponse['productsEnabled'] | null = null;
  @observable segmentsEnabled: ProfileResponse['exchangesEnabled'] | undefined = !this.isActiveUser
    ? [SegmentType.NSE_FO, SegmentType.NSE_EQ]
    : undefined;

  @observable segmentList: UserSegment[] | null = null;

  // actions
  @action setUserData = (data: ProfileResponse) => {
    this.profile = data.userInfo;
    this.bankList = data.userBankList;
    this.productsEnabled = data.productsEnabled;
    this.segmentsEnabled = data.exchangesEnabled;
    this.segmentList = data.segmentList;
    this.ucc = data.userInfo.ucc;
  };

  @action setUserPreferenceSettings = (
    preferenceSettings: UserPreferenceSettings,
  ) => {
    this.userPreferenceSettings = preferenceSettings;
  };

  @action setProductEnabled = (product: Product) => {
    if (this.productsEnabled) {
      this.productsEnabled = [...this.productsEnabled, product];
    }
  }

  @action setUcc = (ucc: string) => {
    this.ucc = ucc;
  };

  @action setProfileID = (profileId: string) => {
    this.profileId = profileId;
  };

  @action setUserType = (type: UserType) => {
    this.userType = type;
  };

  @action setCustomerStatus = (status: CustomerStatus) => {
    this.customerStatus = status;
  };

  @action setIsDepthDataAvailable = (value: boolean) => {
    this.isDepthDataAvailable = value;
  };

  @computed get primaryBank() {
    if (!this.bankList) return null;

    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.bankList.find(({ isPrimaryTrading }) => isPrimaryTrading)!;
  }

  @computed get userFullName() {
    if (!this.profile) return '';

    const { firstName, lastName } = this.profile;

    return `${firstName} ${lastName}`;
  }

  @computed get isActiveUser(): boolean {
    return this.userType === 'CUSTOMER';
  }

  isSegmentEnabled = computedFn((segment: SegmentType) => {
    if (!this.segmentsEnabled || !segment) return false;

    if (segment.includes('INDEX')) return false;

    return !!this.segmentsEnabled.find(
      enabledSegment => enabledSegment === segment,
    );
  });

  isUserDormantForSegment = (segment: SegmentType) => {
    const userSegmentList = this?.segmentList;

    if (!userSegmentList || !userSegmentList?.length) {
      return false;
    }

    const userSegment = userSegmentList?.find(
      s => s.exchange === segment,
    );
    const DORMANT_STATUS = ['SOFT_DORMANT', 'DORMANT'];

    if (userSegment && DORMANT_STATUS.includes(userSegment?.status)) {
      return true;
    }

    return false;
  };
}
